import { useEffect, useId, useRef, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Segment = "home_machine" | "business";

export function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [segment, setSegment] = useState<Segment | "">("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const thanksRef = useRef<HTMLParagraphElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);
  const errorId = useId();
  const noteId = useId();

  useEffect(() => {
    if (done) thanksRef.current?.focus();
  }, [done]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;

    const field = emailRef.current;
    if (!field || !field.checkValidity()) {
      setError("Enter a valid email address.");
      field?.focus();
      return;
    }

    setPending(true);
    setError("");

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          segment: segment || null,
          stitchfinch_hp: honeypotRef.current?.value ?? "",
        }),
      });

      let payload: { ok?: boolean; error?: string } = {};
      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok || payload.ok !== true) {
        setError(payload.error || "Something went wrong. Please try again.");
        field.focus();
        return;
      }

      setDone(true);
    } catch {
      setError("Could not join the waitlist. Check your connection and try again.");
      field.focus();
    } finally {
      setPending(false);
    }
  }

  return (
    <div id="waitlist" className="scroll-mt-8">
      {done ? (
        <p ref={thanksRef} tabIndex={-1} role="status" className="text-base font-medium text-slate-900 outline-none">
          Thanks. You're on the list.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="min-w-0 max-w-full space-y-4" noValidate>
          <div className="sr-only" aria-hidden="true">
            <label htmlFor="stitchfinch_hp">Leave this field blank</label>
            <input
              ref={honeypotRef}
              id="stitchfinch_hp"
              name="stitchfinch_hp"
              className="w-full max-w-full"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              defaultValue=""
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="waitlist-email">Email</Label>
            <Input
              ref={emailRef}
              className="max-w-full"
              id="waitlist-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              disabled={pending}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${errorId} ${noteId}` : noteId}
              placeholder="you@example.com"
              onChange={event => {
                setEmail(event.target.value);
                if (error) setError("");
              }}
            />
          </div>

          <fieldset className="min-w-0 max-w-full space-y-2">
            <legend className="text-sm font-medium text-slate-900">
              What brings you here? <span className="font-normal text-slate-500">Optional</span>
            </legend>
            <div className="flex flex-col gap-2">
              <Label className="min-h-11 min-w-0 max-w-full items-start gap-3 whitespace-normal font-normal leading-5 text-slate-700">
                <input
                  type="radio"
                  name="segment"
                  value="home_machine"
                  className="mt-0.5 size-4 shrink-0 accent-primary"
                  checked={segment === "home_machine"}
                  disabled={pending}
                  onChange={() => setSegment("home_machine")}
                />
                I sew on a home machine (e.g. Brother)
              </Label>
              <Label className="min-h-11 min-w-0 max-w-full items-start gap-3 whitespace-normal font-normal leading-5 text-slate-700">
                <input
                  type="radio"
                  name="segment"
                  value="business"
                  className="mt-0.5 size-4 shrink-0 accent-primary"
                  checked={segment === "business"}
                  disabled={pending}
                  onChange={() => setSegment("business")}
                />
                I order logos for a business
              </Label>
            </div>
          </fieldset>

          {error ? (
            <p id={errorId} role="alert" className="text-sm font-medium text-red-700">
              {error}
            </p>
          ) : null}

          <Button type="submit" size="lg" className="h-auto min-h-12 w-full min-w-0 max-w-full whitespace-normal px-3 text-base font-semibold" disabled={pending} aria-busy={pending}>
            {pending ? "Joining…" : "Join the waitlist"}
          </Button>
          <p id={noteId} className="text-sm leading-6 text-slate-500">
            We'll only email you about StitchFinch.{" "}
            <a href="/privacy" className="underline decoration-slate-300 underline-offset-2 hover:text-slate-700">
              Privacy policy
            </a>.
          </p>
        </form>
      )}
    </div>
  );
}
